import './TenantPage.css'
import { useApi } from '@/hooks/useApi'
import { routes } from '@/utilities/routes'
import { feedListSpec, tenantGetSpec, tenantListMineSpec, TenantUserRole, UserRole, type FeedListItem, type PagedRows, type TenantExternalIds, type TenantRow } from 'aevocam-rest-api-spec'
import { BasicPage } from '@/components/layout/BasicPage'
import { Button } from '@/components/action/Button'
import { PUBLIC_WEBSITE } from '@/utilities/envVariables'
import { useCallback, useEffect, useState } from 'react'
import { useTenantId } from '@/hooks/useTenantId'
import { useInterval } from '@/hooks/useInterval'
import { Link } from 'react-router-dom'
import { FeedTile } from './FeedTile'
import { Notice } from '@/components/action/Notice'

export function TenantPage() {

  const tenantId = useTenantId()
  const api = useApi({
    requireLogin: true,
  })

  const userRole = api.status.session?.userRole
  const tenantUserRole = api.status.session?.tenantUserRole

  const canEdit =
    userRole === UserRole.Admin ||
    tenantUserRole === TenantUserRole.Owner ||
    tenantUserRole === TenantUserRole.Admin

  const canAdmin = userRole === UserRole.Admin ||
    tenantUserRole === TenantUserRole.Owner ||
    tenantUserRole === TenantUserRole.Admin

  const [tenant, setTenant] = useState<TenantRow | null>(null)
  const [feeds, setFeeds] = useState<PagedRows<FeedListItem>>({
    totalRows: 0,
    rows: [],
  })

  //--------------------------------------------------------------------------------
  const refreshFeeds = useCallback(async () => {
    if (tenantId == null) return
    const res = await api.request(feedListSpec, {
      path: {
        tenant: tenantId.toString(),
      },
    })
    setFeeds(res.data?.feeds ?? {
      totalRows: 0,
      rows: [],
    })
  }, [api, tenantId])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    refreshFeeds()
  }, [tenantId, api, refreshFeeds])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (tenantId == null) return
    api.request(tenantGetSpec, { path: { tenant: tenantId.toString() } }).then(res => {
      setTenant(res.data?.tenant ?? null)
    })
  }, [tenantId, api])

  useInterval(refreshFeeds, 60_000)

  //--------------------------------------------------------------------------------
  return (
    <BasicPage
      title={'Tenant: ' + (tenant?.name ?? '(unknown)')}
      className='TenantPage'
      toolbarButtons={<>
        <Button
          label='My account'
          icon='chevronLeft'
          href={routes.home()}
        />
        <Button
          label='Aevocam site'
          icon='home'
          href={PUBLIC_WEBSITE}
        />
        <Button
          label='Log out'
          icon='lock'
          href={routes.logOut(tenantId)}
        />
        <Button
          label='New feed'
          icon='plus'
          disabled={!canEdit}
          href={routes.feedEdit(tenantId!, 'new')}
        />
        {canAdmin && (<>
          <Button
            label='Settings'
            icon='gear'
            disabled={!canAdmin}
            href={routes.tenantEdit(tenantId!)}
          />
          <Button
            label='Invoices'
            icon='fileInvoiceDollar'
            disabled={!canAdmin}
            href={routes.tenantInvoices(tenantId!)}
          />
        </>)}
      </>}
    >

      {tenant?.disabled && (
        <Notice variant='warning' icon='exclamationTriangle'>
          This tenant is disabled.
        </Notice>
      )}

      {!tenant?.disabled && !tenant?.allowIngest && (
        <Notice variant='warning' icon='exclamationTriangle'>
          Photo ingestion not enabled.
          {!(tenant?.externalIds as TenantExternalIds)?.stripe?.paymentMethodId && (<>
            <br />
            You need to <Link to={routes.tenantInvoices(tenantId!)}>
              enroll in automatic billing
            </Link> via Stripe.
          </>)}
        </Notice>
      )}

      <div className='FlexBricks'>

        <div className='Cutout Wrapped'>
          <h2>Camera feeds</h2>

          {feeds.rows.length === 0
            ? (
              <div>
                <p><i>
                  No camera feeds yet.
                </i></p>
                <Button
                  label='New feed'
                  icon='plus'
                  href={routes.feedEdit(tenantId!, 'new')}
                />
              </div>
            )
            : (
              <div className='FeedList'>
                {feeds.rows.map(row => (
                  <FeedTile
                    key={row.feed.id}
                    tenantId={tenantId!}
                    row={row}
                  />
                ))}
              </div>
            )
          }

        </div>

      </div>

    </BasicPage>
  )
}
